import React, { useEffect, useMemo } from 'react';
import { useAwaitControl } from 'react-redux-await-control';
import { InstallmentsTable } from '~/components/Payments/InstallmentsTable/InstallmentsTable';
import { getPaymentsByContractId } from '~/store/contracts/actions';
import { getPersonDetail } from '~/store/people/actions';
import { InstallmentPayment } from '~/typings/entities/Payments';
import { PaymentStatus } from '~/typings/enums/Payment';
import { LOAN_TYPE_NAMES } from '~/typings/enums/LoanType';
import { Flex, Typography } from '~/ui/components';
import { useContractCondition } from '~/screens/ContractDetails/condition';

export type PaymentsProps = {
  contractId: string;
};

export function Payments({ contractId }: PaymentsProps) {
  const paymentsControl = useAwaitControl(getPaymentsByContractId);
  const { selectedCondition } = useContractCondition();
  const conditionPayments = selectedCondition?.details;
  const fetchedPayments: InstallmentPayment = paymentsControl.result();
  const payments: InstallmentPayment = conditionPayments || fetchedPayments;
  const paymentsLoading = conditionPayments ? false : paymentsControl.isRunning();

  const personDetailControl = useAwaitControl(getPersonDetail);
  const personDetail = personDetailControl.result();

  const isConsigPriv = fetchedPayments?.product?.name === LOAN_TYPE_NAMES.CONSIGPRIV;
  const personName = fetchedPayments?.customer?.name || personDetail?.name;
  const personId: number | undefined = personDetail?.id;

  const flatInstallments = useMemo(
    () => payments?.installments?.map((installment) => ({ ...installment, ccb: payments.ccb })) || [],
    [payments],
  );

  const paidCount = useMemo(
    () => flatInstallments?.filter((installment) => installment.status === PaymentStatus.PAID)?.length || '-',
    [flatInstallments],
  );

  const openCount = useMemo(
    () => flatInstallments?.filter((installment) => installment.status === PaymentStatus.OPEN)?.length || '-',
    [flatInstallments],
  );

  const overdueCount = useMemo(
    () => flatInstallments?.filter((installment) => installment.status === PaymentStatus.OVERDUE)?.length || '-',
    [flatInstallments],
  );

  useEffect(() => {
    if (contractId) {
      paymentsControl.start({ contractId });
    }
  }, [contractId]);

  return (
    <Flex direction="column" ph={16} gap={16} align="center" width="100%">
      <Flex
        bc="element.disabled"
        bw={1}
        pv={18}
        ph={8}
        align="center"
        justify="center"
        radius={8}
        bg="background.secondary"
        gap={8}
      >
        <Flex align="center" justify="center" gap={16} grow={1} shrink={1}>
          <Typography color="element.secondary" type="bodySmall" weight={700}>
            Total de parcelas pagas:{' '}
          </Typography>
          <Typography type="bodyLarge" weight={700}>
            {paidCount}
          </Typography>
        </Flex>

        <Flex align="center" justify="center" gap={16} grow={1} shrink={1}>
          <Typography color="element.secondary" type="bodySmall" weight={700}>
            Total de parcelas abertas:{' '}
          </Typography>
          <Typography type="bodyLarge" weight={700}>
            {openCount}
          </Typography>
        </Flex>

        <Flex align="center" justify="center" gap={16} grow={1} shrink={1}>
          <Typography color="element.secondary" type="bodySmall" weight={700}>
            Total de parcelas vencidas:{' '}
          </Typography>
          <Typography type="bodyLarge" weight={700}>
            {overdueCount}
          </Typography>
        </Flex>
      </Flex>

      <InstallmentsTable
        data={flatInstallments}
        loading={paymentsLoading}
        hiddenColumns={['ccb']}
        showPayOverdue={isConsigPriv}
        contractId={contractId}
        personName={personName}
        personId={personId}
      />
    </Flex>
  );
}
